import { Format } from "silver";
import { LANGUAGE_TITLES, LanguageCodes } from "core/Data/Generated/GeoLanguage";
import { generateId } from "shared/functions";

const fmt = new Format;
export { generateId, LanguageCodes };

// jshint bitwise:false
const ATOS_LABELS: Record<number, string> = {
	0:  "0",
	1:  "1",
	2:  "2",
	3:  "3",
	4:  "4",
	5:  "5",
	6:  "6",
	7:  "7",
	8:  "8",
	9:  "9",
	10: "10+"
};

const TC_BOOK_CATEGORIES: Record<number, string> = {
	1:   "KLM Middle School",
	27:  "NOPQ Middle School",
	51:  "RST Middle School",
	75:  "GH",
	78:  "CDEF",
	80:  "EF",
	133: "IJ",
	156: "Kindergarten",
	178: "KLM",
	204: "NOPQ",
	217: "RST",
	241: "UVW",
	268: "XYZ"
};

export const Misc = {
	ROLES: {
		STUDENT:        2,
		BUILDING_ADMIN: 5,
		DISTRICT_ADMIN: 6,
		FACULTY:        7,
		SPECIALIST:     8
	},
	FilterIcons: {
		atos:          "fIcon -atos",
		audio:         "fIcon -audio",
		bookCategory:  "fIcon -bookCategory",
		fiction:       "fIcon -fiction",
		gradeLevel:    "fIcon -gradeLevel",
		guidedReading: "fIcon -guidedReading",
		language:      "fIcon -language",
		length:        "fIcon -length",
		lexile:        "fIcon -lexile",
		scope:         "fIcon -scope",
		search:        "fIcon -search",
		taskType:      "fIcon -taskType",
		text:          "fIcon -search"
	},
	Goals: {
		PERCENT_OF_DAYS_WITH_READING: 1,
		AVERAGE_WEEKLY_MINUTES:       2,
		AVERAGE_WEEKLY_PAGES:         3,
		BOOK_COMPLETION_RATIO:        4,
		NON_FICTION_RATIO:            8,
		QUIZ_AVERAGE:                 9,
		QUIZ_QUESTION_TYPE_AVERAGE:   10,
		ON_TARGET_RATIO:              11,
		OUT_OF_SCHOOL_RATIO:          12,
		NON_AUDIO_READING_RATIO:      13,
		NEWS_TIME_READING:            14,
		PERCENT_OF_DAYS_WITH_NEWS:    15
	},

	formatLexile(value: number|null): string {
		if (value === null) {
			return "—";
		}
		return fmt.lexile(value, { rounded: false });
	},

	formatLexileRounded(value: number|null): string {
		if (value === null) {
			return "—";
		}
		return fmt.lexile(value, { rounded: true });
	},

	formatLexileDiff(value: number|null): string {
		if (value === null) {
			return "—";
		}
		return fmt.lexileDiff(value);
	},

	formatZpd(value: number): string {
		return fmt.number(value, { decimalPlaces: 1 });
	},

	formatArQuiz: fmt.options(fmt.nullable(fmt.number), { decimalPlaces: 1 }),

	/**
	 * @deprecated use Myon.Format.percent
	 */
	formatPercent(ratio: number|null, decimalPlaces?: any): string {
		if (ratio === null) {
			return "—";
		}
		if (isNaN(Number(decimalPlaces))) {
			decimalPlaces = 0;
		}
		return fmt.percent(ratio, { decimalPlaces });
	},

	nullableReturn<R, F, S extends any[], O>(returnVal: R, formatter: (first: F, ...rest: S) => O): (first: F|null, ...args: S) => O|R {
		return (first: F|null, ...args: S): O|R => {
			if (first === null) {
				return returnVal;
			}
			return formatter(first, ...args);
		};
	},

	nullableFormatter(formatter: (...args: any[]) => string): (...args: any[]) => string {
		return Misc.nullableReturn("—", formatter);
	},

	roundToNearest(value: number, nearest: number): number {
		const direction = value >= 0 ? 1 : -1;
		return direction * Math.round(Math.abs(value) / nearest) * nearest;
	},

	guidedReadingLevel(value: number|string): string {
		return (parseInt(String(value), 10) + 10).toString(36).toUpperCase();
	},

	/**
	 * @deprecated use Myon.Format.number
	 */
	formatNumber(value: number, decimalPlaces: number = 0, withCommas: boolean = true): string {
		return fmt.number(value, { decimalPlaces, withCommas });
	},

	formatLargeFloat(value: null | number, significantDigits: number = 3) {
		if (value === null) {
			return "—";
		}
		const absValue = Math.abs(value);
		if (absValue >= 1_000_000_000_000_000) {
			return value.toPrecision(3);
		}
		const digits = absValue < 10 ? 1 : (Math.floor(Math.log(absValue) * Math.LOG10E));
		const thousands = Math.floor(digits / 3);
		const extraDigits = thousands * 3 + (significantDigits - digits);
		const valueStr = (value / Math.pow(1000, thousands)).toFixed(extraDigits - 1);
		return (valueStr) + (["", "k", "m", "b", "t"][thousands] || "");
	},

	formatLargeInt(value: null | number, significantDigits: number = 3): string {
		if (value === null) {
			return "—";
		}
		if (Math.abs(value) < 1000) {
			return value.toFixed(0);
		}
		return Misc.formatLargeFloat(value, significantDigits);
	},

	/**
	 * @deprecated
	 */
	formatInt(value: number): string {
		return fmt.int(value);
	},

	/**
	 * @deprecated
	 */
	formatFloat(value: number): string {
		return fmt.number(value, { decimalPlaces: 1 });
	},

	/** @deprecated */
	secondsToHMS(seconds: number): string {
		return fmt.hhmmss(seconds);
	},

	formatDateLocale(dateObj: Date): string {
		if (! dateObj.toLocaleDateString) {
			return fmt.date(dateObj);
		}
		return dateObj.toLocaleDateString(document.documentElement.lang || "", {
			month: "numeric",
			day:   "numeric",
			year:  "numeric"
		});
	},

	/** @deprecated */
	formatDateUTC(dateObj: Date): string {
		return fmt.dateUTC(dateObj);
	},

	formatTimezone(dateObj: Date): string {
		const match = dateObj.toString().match(/\(([a-z]+)\)/i);
		if (!match) {
			return "–";
		}
		return match[1];
	},

	formatTimestamp(timestamp: number|null): string {
		if (timestamp === null) {
			return "—";
		}
		return fmt.date(new Date(timestamp * 1000));
	},

	formatTimestampUTC(timestamp: number|null): string {
		if (timestamp === null) {
			return "—";
		}
		return fmt.dateUTC(new Date(timestamp * 1000));
	},

	formatTimestampUTCLocale(timestamp: number|null): string {
		if (timestamp === null) {
			return "—";
		}
		const date = new Date(timestamp * 1000);
		if (! date.toLocaleDateString) {
			return fmt.dateUTC(date);
		}
		return date.toLocaleDateString(document.documentElement.lang || "", {
			month:    "numeric",
			day:      "numeric",
			year:     "numeric",
			timeZone: "UTC"
		});
	},

	parseStringAsDate(input: string|null): Date|null {
		if (input === null) {
			return null;
		}
		if (input.indexOf(" ") < 0) {
			return new Date(input + "T00:00:00Z");
		}
		return new Date(input.replace(" ", "T") + "Z");
	},

	parseStringAsTimestamp(input: string|null): number|null {
		const date = Misc.parseStringAsDate(input);
		if (date === null) {
			return null;
		}
		return Math.floor(date.getTime() / 1000);
	},

	/** @deprecated use Myon.Format.dateTime */
	formatDateTime(dateObj: Date|null): string {
		if (! dateObj) {
			return "—";
		}
		return fmt.dateTime(dateObj);
	},

	atosLabel(i: number): string {
		return ATOS_LABELS[i] || "";
	},

	languageTitle(id: number): string {
		return LANGUAGE_TITLES[id] || "";
	},
	tcBookCategory(id: number): string {
		return TC_BOOK_CATEGORIES[id] || "";
	},

	regexpEscape(r: string): string {
		return r.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
	}
};

export function explodeFilterText(text: string): string[] {
	const filterMatches = text.toLowerCase().match(/("[^"]*"|[^\s]+)/g);
	if (!filterMatches) {
		return [];
	}
	return filterMatches.map((str) => str.replace(/^["\s]+(.*?)["\s]+$/, "$1"));
}

export function textMatchesFilter(compareText: string, filterTokens: string[]): boolean {
	compareText = compareText.toLowerCase().trim().replace(/([\s]{2,})/g, " ");
	for (let i = 0; i < filterTokens.length; i++) {
		if (compareText.indexOf(filterTokens[ i ]) === -1) {
			return false;
		}
	}
	return true;
}
